import { useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  Clock3,
  Eye,
  MousePointerClick,
  RefreshCw,
  Smartphone,
  TrendingUp,
  Users,
  Waves,
} from "lucide-react";
import { usePageAnalytics, usePageHealth } from "@/hooks/use-self-learning";

type AnalyticsPeriod = "7d" | "30d";

interface PageAnalyticsPanelProps {
  pageId: string | undefined;
}

function formatPercent(value: number | null | undefined, digits = 0): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Unavailable";
  }

  return `${value.toFixed(digits)}%`;
}

function formatRatioPercent(value: number | null | undefined, digits = 1): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Unavailable";
  }

  return `${(value * 100).toFixed(digits)}%`;
}

function formatSeconds(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Unavailable";
  }

  if (value >= 60) {
    const minutes = Math.floor(value / 60);
    const seconds = Math.round(value % 60);
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }

  return `${Math.round(value)}s`;
}

function MetricCard({ label, value, hint, icon: Icon }: {
  label: string;
  value: string;
  hint: string;
  icon: typeof Eye;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold tabular-nums text-[var(--color-foreground)]">{value}</div>
      <p className="mt-1 text-[11px] leading-5 text-[var(--color-muted-foreground)]">{hint}</p>
    </div>
  );
}

export default function PageAnalyticsPanel({ pageId }: PageAnalyticsPanelProps) {
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const analyticsQuery = usePageAnalytics(pageId);
  const healthQuery = usePageHealth(pageId);

  const handleRefresh = () => {
    analyticsQuery.refresh();
    healthQuery.refresh();
  };

  const selectedWindow = useMemo(() => {
    const telemetry = analyticsQuery.analytics?.pageSignals.visitorTelemetry;
    if (!telemetry) {
      return null;
    }

    return period === "7d" ? telemetry.windows.last7Days : telemetry.windows.last30Days;
  }, [analyticsQuery.analytics, period]);

  if (!pageId) {
    return (
      <div className="space-y-4 p-4">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 text-sm text-[var(--color-muted-foreground)]">
          Save the page first so the editor can attach analytics to a real page id and published telemetry.
        </div>
      </div>
    );
  }

  if (analyticsQuery.isLoading || healthQuery.isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 text-sm text-[var(--color-muted-foreground)]">
          Loading measured page analytics…
        </div>
      </div>
    );
  }

  if (!analyticsQuery.analytics) {
    return (
      <div className="space-y-3 py-12 text-center">
        <BarChart3 className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)]/30" />
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {analyticsQuery.error ?? "Publish the page to unlock measured page analytics."}
        </p>
      </div>
    );
  }

  const analytics = analyticsQuery.analytics;
  const health = healthQuery.health;
  const telemetry = analytics.pageSignals.visitorTelemetry;

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">
            Measured Analytics
          </p>
          <h2 className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">Editor Analytics</h2>
          <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">
            Live page views come from `page.viewed` touchpoints. Time on page, scroll depth, CTA clicks, source mix, and device mix come from the published runtime session beacon.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[10px] text-[var(--color-muted-foreground)] transition hover:text-[var(--color-foreground)]"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-foreground)]">
          <Activity className="h-4 w-4 text-[var(--color-primary)]" />
          Health score {health?.overallScore ?? "Unavailable"}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--color-border)] p-0.5">
          {([
            { value: "7d", label: "7d" },
            { value: "30d", label: "30d" },
          ] as const).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={`rounded-md px-2 py-1 text-xs transition-colors ${
                period === option.value
                  ? "bg-[var(--color-primary)]/10 font-medium text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)] hover:bg-accent"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          label="Page Views"
          value={(selectedWindow?.pageViews ?? 0).toLocaleString()}
          hint={`${analytics.summary.totalPageViews.toLocaleString()} all-time live views`}
          icon={Eye}
        />
        <MetricCard
          label="Unique Visitors"
          value={selectedWindow?.uniqueVisitors?.toLocaleString() ?? "Unavailable"}
          hint="Measured only when analytics cookie consent exists."
          icon={Users}
        />
        <MetricCard
          label="Bounce Rate"
          value={formatRatioPercent(selectedWindow?.bounceRate)}
          hint="Short-session approximation from measured page-session telemetry."
          icon={TrendingUp}
        />
        <MetricCard
          label="Avg. Time"
          value={formatSeconds(selectedWindow?.avgTimeOnPageSeconds)}
          hint={healthQuery.error ?? "Measured from real page session duration beacons."}
          icon={Clock3}
        />
        <MetricCard
          label="Avg. Scroll"
          value={formatPercent(selectedWindow?.avgScrollDepthPercent, 0)}
          hint="Average max page depth reached in the selected window."
          icon={Waves}
        />
        <MetricCard
          label="Conv. Rate"
          value={formatRatioPercent(health?.telemetry.pageConversionRate)}
          hint={`${health?.telemetry.directConversions ?? analytics.summary.totalDirectFormSubmissions} measured direct conversions`}
          icon={MousePointerClick}
        />
      </div>

      <section className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          <BarChart3 className="h-3.5 w-3.5" />
          Traffic Sources
        </div>
        <div className="space-y-2">
          {telemetry.trafficSources.length === 0 ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
              No measured source mix yet for this page window.
            </div>
          ) : telemetry.trafficSources.map((source) => (
            <div key={source.source} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-[var(--color-foreground)]">{source.source}</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">{source.views} views</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-[var(--color-primary)]/10">
                <div className="h-full rounded-full bg-[var(--color-primary)]" style={{ width: `${Math.max(4, source.sharePercent)}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">{source.sharePercent.toFixed(1)}% share</div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          <Smartphone className="h-3.5 w-3.5" />
          Device Split
        </div>
        <div className="space-y-2">
          {telemetry.deviceSplit.length === 0 ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
              No measured device mix yet for this page window.
            </div>
          ) : telemetry.deviceSplit.map((device) => (
            <div key={device.device} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium capitalize text-[var(--color-foreground)]">{device.device}</span>
                <span className="text-xs text-[var(--color-muted-foreground)]">{device.views} views</span>
              </div>
              <div className="mt-1 text-[11px] text-[var(--color-muted-foreground)]">{device.sharePercent.toFixed(1)}% share</div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          <Waves className="h-3.5 w-3.5" />
          Section Scroll Heatmap
        </div>
        <div className="space-y-2">
          {telemetry.scrollDepthByComponent.length === 0 ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
              Scroll-depth samples have not been captured for this page yet.
            </div>
          ) : telemetry.scrollDepthByComponent.map((section) => (
            <div key={section.componentId} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-[var(--color-foreground)]">{section.componentType ?? "Section"}</div>
                  <div className="text-[11px] text-[var(--color-muted-foreground)]">{section.componentId}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-[var(--color-foreground)]">{formatPercent(section.avgScrollDepthPercent, 0)}</div>
                  <div className="text-[11px] text-[var(--color-muted-foreground)]">{section.sampleCount} samples</div>
                </div>
              </div>
              <div className="mt-2 h-2 rounded-full bg-amber-100/70 dark:bg-amber-500/10">
                <div
                  className="h-full rounded-full bg-amber-500"
                  style={{ width: `${Math.max(4, section.avgScrollDepthPercent)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
          <MousePointerClick className="h-3.5 w-3.5" />
          CTA Click Rates
        </div>
        <div className="space-y-2">
          {telemetry.ctaClicks.length === 0 ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
              No measured CTA clicks yet for this page.
            </div>
          ) : telemetry.ctaClicks.map((cta) => (
            <div key={cta.componentId} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-[var(--color-foreground)]">{cta.label ?? cta.componentType ?? "CTA"}</div>
                  <div className="text-[11px] text-[var(--color-muted-foreground)]">{cta.componentId}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-[var(--color-foreground)]">{cta.clicks.toLocaleString()} clicks</div>
                  <div className="text-[11px] text-[var(--color-muted-foreground)]">{formatRatioPercent(cta.clickThroughRate)}</div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">
                {cta.sessionsWithClicks} sessions with at least one CTA interaction
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
